import { useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { PrayerTime } from "@/types/page";
import { useDatabaseOperations } from "./useDatabaseOperations";
import { useContentFormatting } from "./useContentFormatting";

interface UseSaveContentProps {
  prayerTimes: PrayerTime[];
  blockTitle: string;
  animationSpeed: number;
  isLegacyFormat: boolean;
  originalContentFormat: any;
  onContentChange: (field: string, value: any) => void;
  setHasChanges: (value: boolean) => void;
  content: any;
  currentUser: any;
}

export function useSaveContent({
  prayerTimes,
  blockTitle,
  animationSpeed,
  isLegacyFormat,
  originalContentFormat,
  onContentChange,
  setHasChanges,
  content,
  currentUser,
}: UseSaveContentProps) {
  const { toast } = useToast();
  const { savePrayerTimesToDatabase } = useDatabaseOperations({ currentUser });
  const { formatContentForSaving } = useContentFormatting();

  // Create a save function that can take explicit parameters to avoid closure issues
  const saveContent = useCallback(async (
    currentPrayerTimes?: PrayerTime[], 
    currentBlockTitle?: string, 
    currentAnimationSpeed?: number
  ) => {
    try {
      console.log("Saving prayer times content...");
      // Use the provided values or fall back to state values
      const prayerTimesToSave = currentPrayerTimes || prayerTimes;
      const blockTitleToSave = currentBlockTitle || blockTitle;
      const animationSpeedToSave = currentAnimationSpeed !== undefined ? currentAnimationSpeed : animationSpeed;
      
      console.log("Current state:", {
        prayerTimes: prayerTimesToSave,
        blockTitle: blockTitleToSave,
        animationSpeed: animationSpeedToSave,
        isLegacyFormat,
        originalContentFormat
      });

      // Skip saving if prayer times is empty and there are prayer times in content
      // This likely means we caught an intermediate state update
      if (prayerTimesToSave.length === 0 && 
          ((content.prayerTimes && content.prayerTimes.length > 0) || 
           (content.sections && content.sections[0]?.prayerTimes?.length > 0))) {
        console.warn("Skipping save with empty prayer times when content has prayer times");
        return;
      }

      // Format the content based on the original format
      const formattedContent = formatContentForSaving(
        prayerTimesToSave,
        blockTitleToSave,
        animationSpeedToSave,
        isLegacyFormat,
        originalContentFormat
      );
      
      // Apply all changes to the content
      Object.entries(formattedContent).forEach(([key, value]) => {
        console.log(`Page content field "${key}" changed:`, value);
        onContentChange(key, value);
      });
      
      // Save data to the database as well
      await savePrayerTimesToDatabase(
        blockTitleToSave,
        animationSpeedToSave,
        prayerTimesToSave
      );
      
      setHasChanges(false);
      
      // Show save notification
      toast({
        title: "נשמר בהצלחה",
        description: "השינויים נשמרו בהצלחה",
      });
    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        title: "שגיאה",
        description: "אירעה שגיאה בעת שמירת השינויים",
        variant: "destructive",
      });
    }
  }, [
    blockTitle,
    prayerTimes,
    animationSpeed,
    onContentChange,
    toast,
    isLegacyFormat,
    originalContentFormat,
    currentUser,
    content,
    savePrayerTimesToDatabase,
    formatContentForSaving,
    setHasChanges
  ]);

  return {
    saveContent,
  };
}
